import React, { Component } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button, Box, Typography, Alert } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';

class BarcodeScanner extends Component {
    constructor(props) {
        super(props);
        this.state = {
            scanning: false,
            error: null,
            cameraId: null,
            torchOn: false,
            torchSupported: false
        };
        this.html5QrCode = null;
        this.scannerId = 'barcode-scanner-' + Math.random().toString(36).substr(2, 9);
    }

    componentWillUnmount() {
        this.stopScanning();
    }

    startScanning = async () => {
        try {
            this.setState({ scanning: true, error: null });

            // Initialize scanner if not already done
            if (!this.html5QrCode) {
                this.html5QrCode = new Html5Qrcode(this.scannerId);
            }

            // Get cameras
            const devices = await Html5Qrcode.getCameras();
            if (!devices || devices.length === 0) {
                throw new Error('No cameras found on this device');
            }

            // Prefer back camera (environment facing) for mobile
            const backCamera = devices.find(device => 
                device.label.toLowerCase().includes('back') || 
                device.label.toLowerCase().includes('environment')
            );
            const cameraId = backCamera ? backCamera.id : devices[0].id;

            this.setState({ cameraId });

            // Start scanning
            await this.html5QrCode.start(
                cameraId,
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0,
                    // Use back camera on mobile
                    facingMode: { ideal: 'environment' }
                },
                this.onScanSuccess,
                this.onScanFailure
            );

            // Check torch support
            const capabilities = this.html5QrCode.getRunningTrackCapabilities();
            const torchSupported = capabilities && capabilities.torch;
            this.setState({ torchSupported });

        } catch (err) {
            console.error('Error starting scanner:', err);
            this.setState({ 
                error: err.message || 'Failed to start camera. Please check permissions.',
                scanning: false 
            });
        }
    };

    stopScanning = async () => {
        try {
            if (this.html5QrCode && this.html5QrCode.isScanning) {
                await this.html5QrCode.stop();
            }
            this.setState({ scanning: false, torchOn: false });
        } catch (err) {
            console.error('Error stopping scanner:', err);
        }
    };

    toggleTorch = async () => {
        try {
            if (this.html5QrCode && this.state.torchSupported) {
                const track = this.html5QrCode.getRunningTrackSettings();
                await track.applyConstraints({
                    advanced: [{ torch: !this.state.torchOn }]
                });
                this.setState({ torchOn: !this.state.torchOn });
            }
        } catch (err) {
            console.error('Error toggling torch:', err);
        }
    };

    onScanSuccess = (decodedText, decodedResult) => {
        console.log('Barcode detected:', decodedText);
        
        // Stop scanning after successful read
        this.stopScanning();
        
        // Call parent callback with the barcode
        if (this.props.onScanSuccess) {
            this.props.onScanSuccess(decodedText, decodedResult);
        }
    };

    onScanFailure = (error) => {
        // This gets called constantly when no barcode is in view - ignore it
        // Only log actual errors
        if (error && !error.includes('NotFoundException')) {
            console.warn('Scan error:', error);
        }
    };

    render() {
        const { scanning, error, torchSupported, torchOn } = this.state;

        return (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Scanner viewport */}
                <Box 
                    id={this.scannerId}
                    sx={{ 
                        width: '100%', 
                        maxWidth: '500px',
                        minHeight: scanning ? '350px' : '0px',
                        borderRadius: 3,
                        overflow: 'hidden',
                        position: 'relative',
                        bgcolor: scanning ? 'grey.900' : 'transparent',
                        boxShadow: scanning ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
                        '& video': {
                            borderRadius: 3
                        }
                    }}
                />

                {/* Scanner instructions */}
                {scanning && (
                    <Box sx={{ 
                        textAlign: 'center',
                        p: 2,
                        bgcolor: 'primary.light',
                        borderRadius: 2,
                        color: 'white',
                        width: '100%',
                        maxWidth: '500px'
                    }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            📸 Point camera at barcode
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.9 }}>
                            Detection happens automatically
                        </Typography>
                    </Box>
                )}

                {/* Control buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
                    {!scanning ? (
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<CameraAltIcon />}
                            onClick={this.startScanning}
                            sx={{ minWidth: 200, py: 1.5 }}
                        >
                            Start Camera
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<StopCircleIcon />}
                                onClick={this.stopScanning}
                                sx={{ minWidth: 140 }}
                            >
                                Stop Scanning
                            </Button>

                            {torchSupported && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={torchOn ? <FlashlightOffIcon /> : <FlashlightOnIcon />}
                                    onClick={this.toggleTorch}
                                    sx={{ minWidth: 140 }}
                                >
                                    {torchOn ? 'Light Off' : 'Light On'}
                                </Button>
                            )}
                        </>
                    )}
                </Box>

                {!scanning && (
                    <Box sx={{ 
                        textAlign: 'center',
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        width: '100%',
                        maxWidth: '500px'
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            💡 Make sure to allow camera access when prompted
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }
}

export default BarcodeScanner;
